import { faPrint } from "@fortawesome/free-solid-svg-icons";
import { type FC, useState } from "react";
import { useApiCall } from "../api/api";
import type { ProjectCheckIn } from "../api/dto/ProjectCheckIn";
import { Callout } from "../components/content/Callout";
import { Input } from "../components/form/Input";
import { ModalFooter } from "../components/layout/ModalProvider";
import { printCheckInCode } from "../util/printCheckInCode";

/** How long a printed code may be valid, in hours (the backend's maximum). */
const DEFAULT_PRINT_HOURS = 8;
const MAX_PRINT_HOURS = 8;
const isValidPrintHours = (hours: number) =>
  Number.isInteger(hours) && hours >= 1 && hours <= MAX_PRINT_HOURS;

/**
 * Asks for how long the printed QR should be accessible, then opens a printable page for it. The rotating code
 * of the "Code bekijken" modal only lasts a minute, so a printout gets its own code that lasts that many hours.
 */
export const PrintCheckInCodeModal: FC<{ checkIn: ProjectCheckIn; projectName: string }> = (
  props,
) => {
  const { createCheckInPrintCode } = useApiCall();
  const [printHours, setPrintHours] = useState(DEFAULT_PRINT_HOURS);
  const [error, setError] = useState<string | null>(null);

  const print = async () => {
    setError(null);
    // Opened before the request: browsers only allow a window to open directly from a click.
    const printWindow = window.open("", "_blank");
    if (printWindow === null) {
      setError("Afdrukken is mislukt. Sta pop-ups toe voor deze pagina en probeer het opnieuw.");
      throw new Error("Print window was blocked");
    }
    try {
      printCheckInCode(
        printWindow,
        props.projectName,
        await createCheckInPrintCode(props.checkIn.uuid, printHours),
      );
    } catch (e) {
      printWindow.close();
      setError("Afdrukken is mislukt. Is de check-in inmiddels gesloten?");
      throw e;
    }
  };

  return (
    <div className={"flex flex-col space-y-3"}>
      <p className={"text-sm text-slate-600"}>
        Een afdruk heeft een eigen code die zo lang geldig blijft, zodat mensen op locatie hem
        kunnen scannen.
      </p>
      <Input
        type={"number"}
        label={`Afdruk geldig voor (uren, maximaal ${MAX_PRINT_HOURS})`}
        value={printHours}
        onChange={setPrintHours}
        maxWidth={"sm"}
      />
      {error !== null && <Callout color={"danger"}>{error}</Callout>}
      <ModalFooter
        icon={faPrint}
        text={"Code printen"}
        disabled={!isValidPrintHours(printHours)}
        onSubmit={print}
      />
    </div>
  );
};
